import { AudioLines, FileText, PenLine, StickyNote, type LucideIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { DeleteSourceControl } from "@/components/DeleteSourceControl";
import { SourceInsightList } from "@/components/SourceInsightList";
import { PipelinePanel } from "@/pages/PipelinePanel";
import { ApiError, type RecordingInsight, type SourceMaterial } from "@/lib/api";
import { formatBytes, formatDate } from "@/lib/format";
import { usePipelineRun } from "@/lib/usePipelineRun";

const ICONS: Record<string, LucideIcon> = {
  recording: AudioLines,
  document: FileText,
  note: StickyNote,
  chat_log: FileText,
  meeting: AudioLines,
};

const KIND_LABEL: Record<string, string> = {
  recording: "Recording",
  document: "Document",
  note: "Note",
  chat_log: "Chat log",
  meeting: "Meeting",
};

/**
 * What one uploaded source actually is.
 *
 * Deliberately small. CAP-3 requires the duplicate prompt to link to the earlier
 * upload, and the recordings list (UI-4) needs somewhere for a row to lead — so this
 * page is that place, and says the true things about a source: what it is, when it
 * happened, what arrived, and where the pipeline has got it to. The full recording view
 * with its playable transcript, speakers and tabs is UI-5 (M3.1) and is deliberately
 * not anticipated here, because guessing at its shape now is how a screen gets built
 * twice.
 */
export function SourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api, refreshReview } = useApp();

  const [source, setSource] = useState<SourceMaterial | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [insights, setInsights] = useState<RecordingInsight[] | null>(null);
  const pipeline = usePipelineRun(api, id);

  // What was read out of a note or a document (M17.4) — the list INS-7's approve-all sits under.
  // A recording forwards to its own room before this matters.
  const loadInsights = useCallback(() => {
    if (!id) return;
    // Started inside the promise, so a failure of any kind leaves the page as it was — the
    // insights are an addition to what this page says about a source, never a condition of it.
    void Promise.resolve()
      .then(() => api.getRecordingInsights(id))
      .then((loaded) => setInsights(loaded.insights))
      .catch(() => setInsights([]));
  }, [api, id]);

  const runStatus = pipeline.run?.status;
  useEffect(() => {
    if (source && source.kind !== "recording" && source.kind !== "meeting") loadInsights();
  }, [source, runStatus, loadInsights]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    setSource(null);
    setProblem(null);

    void api
      .getSource(id)
      .then((result) => {
        if (!cancelled) setSource(result);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProblem(
          cause instanceof ApiError && cause.status === 404
            ? "There is no source at this address."
            : "That source could not be loaded.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  if (problem) {
    return (
      <div className="px-[var(--space-8)] py-[var(--space-12)] text-center">
        <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">{problem}</p>
      </div>
    );
  }

  // A recording has a room of its own from M3.1 (UI-5), and every link now points at
  // it. This address still resolves because older ones exist — a duplicate prompt sent
  // somebody here, a browser remembered it — so it forwards rather than showing a
  // second, poorer version of the same recording.
  // The query travels with the forward: a `?insight=` link into the old address still
  // lands on the transcript with its spans highlighted (UI-7).
  // A meeting shares the recording detail room too (recording/meeting split) — it is a
  // source_materials row like any other, and the same forward applies.
  if (source && (source.kind === "recording" || source.kind === "meeting") && id)
    return <Navigate to={`/recordings/${id}${window.location.search}`} replace />;

  if (!source) {
    return (
      <p className="px-[var(--space-8)] py-[var(--space-8)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Loading…
      </p>
    );
  }

  const Icon = ICONS[source.kind] ?? FileText;

  return (
    <div className="flex max-w-[940px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex items-start gap-[var(--space-4)]">
        <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)]">
          <Icon size={20} strokeWidth={2} className="text-[var(--vl-bridge)]" />
        </span>

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            {source.title ?? source.originalFilename ?? "Untitled source"}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">
            <span>
              {KIND_LABEL[source.kind] ?? source.kind} · added {formatDate(source.createdAt)}
            </span>
            {/* CAP-8 — set when it was added, and the reason what is approved out of it is pinned. */}
            {source.authoredByOwner && (
              <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--vl-bridge)] px-2.5 py-0.5 text-[length:var(--text-xs)] font-medium text-[var(--vl-bridge)]">
                <PenLine size={12} strokeWidth={2} aria-hidden />
                Written by you
              </span>
            )}
          </p>
        </div>

        <DeleteSourceControl sourceId={source.id} />
      </header>

      <dl className="grid grid-cols-2 gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        <Fact label="File" value={source.originalFilename} />
        <Fact label="Size" value={source.sizeBytes === null ? null : formatBytes(source.sizeBytes)} />
        <Fact
          label={source.kind === "recording" ? "Recorded on" : "Document date"}
          value={formatDate(source.sourceDate) || null}
          absent="Not recorded"
        />
        <Fact
          label="Language"
          value={source.language?.toUpperCase() ?? null}
          absent="Detected during processing"
        />
      </dl>

      <PipelinePanel sourceId={source.id} kind={source.kind} state={pipeline} />

      {insights !== null && insights.length > 0 && (
        <section className="flex flex-col gap-[var(--space-3)]">
          <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
            What was read out of it
          </h2>
          <SourceInsightList
            sourceId={source.id}
            sourceName={source.title ?? source.originalFilename}
            insights={insights}
            onApproved={() => {
              loadInsights();
              refreshReview();
            }}
            onStale={loadInsights}
          />
        </section>
      )}

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        {source.kind === "note"
          ? "A note is stored as it was written and does not change."
          : "The file is stored exactly as it arrived and will not change."}
      </p>
    </div>
  );
}

function Fact({ label, value, absent = "—" }: { label: string; value: string | null; absent?: string }) {
  return (
    <div>
      <dt className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </dt>
      {/* An absent value says what absent means here, rather than showing a blank the
          reader has to interpret. */}
      <dd
        className={
          value
            ? "mt-1 break-words text-[length:var(--text-sm)] text-[var(--text-primary)]"
            : "mt-1 text-[length:var(--text-sm)] italic text-[var(--text-muted)]"
        }
      >
        {value ?? absent}
      </dd>
    </div>
  );
}
